import { Calendar, Wrench } from '@/ui/icons'
import { Link, useNavigate } from 'react-router-dom'
import { Button } from '@/ui/components/'
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/components/'
import { PageLayout } from '@/ui/layout'
import { useAuth } from '@/modules/auth'
import { useDashboard } from '@/modules/dashboard'
import { useVehicles } from '@/modules/vehicles'
import { WaveLoader } from '@/ui/components'
import { TripCalendar, DashboardCover, PendingTripInvitesSection } from '@/modules/dashboard/components'
import { ContributionGraph } from '@/modules/users/components'
import { useUserContributions } from '@/modules/users/hooks/useUserContributions'
import { useFeed, FeedList } from '@/modules/feed'
import { PageMeta } from '@/components/PageMeta'

export default function Dashboard() {
  const { user } = useAuth()
  const { dashboardData, isLoading: dashboardLoading } = useDashboard()
  const { cards, isLoading: feedLoading, error: feedError, refreshFeed } = useFeed()
  const { vehicles, isLoading: vehiclesLoading } = useVehicles()
  const navigate = useNavigate()

  const { contributions, isLoading: isLoadingContributions } = useUserContributions(
    user?.id || ''
  )

  const recentTrips = dashboardData?.recentTrips || []
  const upcomingTrips = dashboardData?.upcomingTrips || []

  // Only block on the very first paint (no data anywhere yet).
  if (dashboardLoading && feedLoading && !dashboardData) {
    return (
      <PageLayout>
        <div className="min-h-screen flex items-center justify-center">
          <WaveLoader />
        </div>
      </PageLayout>
    )
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'TBD'
    const [year, month, day] = dateString.split('-').map(Number)
    const date = new Date(year, month - 1, day)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  const showVehicleCta = !vehiclesLoading && (!vehicles || vehicles.length === 0)
  const userName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Adventurer'

  return (
    <PageLayout>
      <PageMeta title="Feed" description="Your overlanding feed and what to do next" />
      <div className="min-h-screen bg-earth-50 relative overflow-x-hidden">
        <div className="relative w-full">
          <DashboardCover userName={userName} />
        </div>

        <div className="container-app py-6 sm:py-8 relative">
          {/* Vehicle nudge — a transient notice, not a peer content block to the feed */}
          {showVehicleCta && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
              <div className="flex items-center gap-2 text-sm text-earth-700 dark:text-earth-300">
                <Wrench className="w-4 h-4 flex-shrink-0 text-earth-500" />
                <span>Add your vehicle to personalize trip planning and crew details.</span>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/garage')}
                className="w-full sm:w-auto flex-shrink-0"
              >
                Add Vehicle
              </Button>
            </div>
          )}

          <div className="grid lg:grid-cols-12 gap-8">
            {/* Feed — primary surface */}
            <div className="lg:col-span-8 order-1">
              <FeedList
                cards={cards}
                isLoading={feedLoading}
                error={feedError}
                onRefresh={refreshFeed}
              />
            </div>

            {/* Secondary rail — high-value widgets */}
            <div className="lg:col-span-4 order-2 space-y-6">
              <PendingTripInvitesSection />

              {/* Trips — calendar as the primary visual, next-up list as a compact strip beneath */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-foreground">Trips</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="w-full overflow-x-auto">
                    <TripCalendar
                      trips={Array.from(
                        new Map([...recentTrips, ...upcomingTrips].map((t) => [t.id, t])).values()
                      )}
                    />
                  </div>

                  {upcomingTrips.length > 0 ? (
                    <div className="pt-3 border-t border-border">
                      <p className="text-xs font-medium text-earth-700 dark:text-earth-400 mb-2">Next up</p>
                      <div className="space-y-1">
                        {upcomingTrips.slice(0, 3).map((trip: any) => (
                          <Link
                            key={trip.id}
                            to={`/trips/${trip.id}`}
                            className="flex items-center gap-2 py-1.5 -mx-1 px-1 rounded-md text-sm hover:bg-earth-50 dark:hover:bg-earth-800/50 transition-colors"
                          >
                            <Calendar className="w-3.5 h-3.5 text-earth-500 flex-shrink-0" />
                            <span className="truncate flex-1 text-foreground">{trip.name}</span>
                            <span className="text-xs text-earth-700 dark:text-earth-400 flex-shrink-0">
                              {formatDate(trip.start_date)}
                            </span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center pt-3 border-t border-border">
                      <p className="text-sm text-earth-700 dark:text-earth-400 mb-3">No upcoming trips</p>
                      <Button variant="outline" size="sm" asChild>
                        <Link to="/trips/new">Plan one now</Link>
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Activity */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg font-semibold text-foreground">Your Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  {isLoadingContributions ? (
                    <div className="flex items-center justify-center py-8">
                      <WaveLoader />
                    </div>
                  ) : (
                    <div className="w-full overflow-x-auto">
                      <ContributionGraph contributions={contributions} />
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  )
}
